import { useEffect, useRef, type ReactNode } from "react";

/**
 * Componenti d'interfaccia condivisi fra le schermate. Fuori da `App.tsx`
 * perche' i chunk per ruolo (PERF-04) devono poterli importare senza trascinarsi
 * dietro l'app intera.
 */

export function Modal({
  children,
  onClose,
  labelledBy,
}: {
  children: ReactNode;
  onClose: () => void;
  labelledBy: string;
}) {
  const reference = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = reference.current;
    const returnFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    dialog?.showModal();
    const focusables = () =>
      Array.from(
        dialog?.querySelectorAll<HTMLElement>("button, input, textarea, select, [tabindex]") ?? [],
      ).filter((element) => !element.hasAttribute("disabled") && element.tabIndex >= 0);
    focusables()[0]?.focus();
    const trapFocus = (event: KeyboardEvent) => {
      const currentFocusables = focusables();
      if (event.key !== "Tab" || currentFocusables.length === 0) return;
      event.preventDefault();
      const currentIndex = currentFocusables.indexOf(document.activeElement as HTMLElement);
      const offset = event.shiftKey ? -1 : 1;
      const nextIndex =
        (currentIndex + offset + currentFocusables.length) % currentFocusables.length;
      currentFocusables[nextIndex]?.focus();
    };
    dialog?.addEventListener("keydown", trapFocus);
    return () => {
      dialog?.removeEventListener("keydown", trapFocus);
      dialog?.close();
      if (returnFocus?.isConnected) returnFocus.focus();
    };
  }, []);
  return (
    <dialog
      ref={reference}
      className="dialog-backdrop"
      aria-labelledby={labelledBy}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="dialog">{children}</div>
    </dialog>
  );
}

export function PageTitle({
  eyebrow,
  title,
  text,
}: {
  eyebrow: string;
  title: string;
  text: string;
}) {
  return (
    <header className="page-title">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      <p>{text}</p>
    </header>
  );
}

export function EmptyState({ title, text }: { title: string; text: string }) {
  return (
    <div className="empty-state">
      <span aria-hidden="true">○</span>
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}
